import React, { LabelHTMLAttributes } from 'react'

import styled from '@emotion/styled'

import { spacing } from '../../../theme/index'
import LoadingIcon from '../../LoadingIcon/LoadingIcon'
import { RadioInputAndLabel } from '../../RadioInput/RadioInput'
import { RadioInputLabelProps } from '../../RadioInputLabel/RadioInputLabel'
import { RadioButton } from '../RadioButton/RadioButton'

export type RadioInputProps = {
  label: React.ReactNode
  loading?: boolean
  name?: string
  value?: string
  id: string
  onClick?: (e: React.SyntheticEvent) => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  StyledLabelComponent?: React.ComponentType<RadioInputLabelProps & LabelHTMLAttributes<HTMLLabelElement>>
} & RadioInputLabelProps

const InlineRadioInputAndLabel = styled(RadioInputAndLabel)`
  width: fit-content;

  input {
    cursor: pointer;
  }
`

const InnerLabel = styled.label<{ checked?: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  cursor: pointer;

  ${props => props.checked && `color: ${props.theme.colors.label};`}
`

const ButtonWrapper = styled.div`
  height: 1.5rem;
  margin-right: ${spacing.smallGap};
`

// Here we sink the RadioInput down below the label so that Cypress can click
// the label text and activate the input. This is a bit silly, but due to the way
// this input is currently structured we don't have a great alternative. If you
// find yourself attempting to tweak this input, consider these requirements:
//
// - Does the radio input have a focus ring?
// - Can we tab to and from the radio input?
// - Can we navigate between radio inputs with the arrow keys?
// - Can we click the label to activate the radio input?
// - Can we use .check() to activate the radio input?
// - When we focus the radio input, is the voiceover correct?
const RadioInput = styled.input`
  z-index: -1;
`

/**
 * @deprecated Use ArcadeRadioInline instead
 */
const RadioInputInline = (props: RadioInputProps) => {
  const { onChange, onClick, checked, value, disabled, label, invalid, id, loading, animate = true, ...rest } = props

  const ariaLabelId = `radio-input-${id}`

  return (
    <InlineRadioInputAndLabel>
      <RadioInput
        data-testid="radio-inline-input"
        type="radio"
        checked={checked}
        id={id}
        disabled={disabled}
        onChange={onChange}
        onClick={onClick}
        value={value}
        tabIndex={0}
        aria-labelledby={ariaLabelId}
        {...rest}
      />
      <InnerLabel id={ariaLabelId} htmlFor={id} checked={checked}>
        <ButtonWrapper>
          {loading ? (
            <LoadingIcon size={24} />
          ) : (
            <RadioButton
              animate={animate}
              aria-checked={checked === true}
              disabled={disabled}
              role="radio"
              checked={checked}
            />
          )}
        </ButtonWrapper>
        {label}
      </InnerLabel>
    </InlineRadioInputAndLabel>
  )
}

RadioInputInline.displayName = 'RadioInputInline'
export default RadioInputInline
